import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LazyImage from "../Shared/LazyImage";
import DeleteButton from "../Buttons/DeleteButton";
import EditButton from "../Buttons/EditButton";
import BaseAlertModal from "../Modals/BaseAlertModal";
import { toCamelCase } from "../../utils/Helpers";
import { longDateTime } from "../../utils/DateHelper";
import { getSortDirectionIcon, toggleSort } from "../../utils/FilterHelpers";
import { IMAGE_MAP } from "../../utils/constants";
import CheckBox from "../CheckBox/CheckBox";

const CardTable = ({
    headers,
    rows,
    isSubmitting,
    handleDelete,
    handleEditClicked,
    purpose,
    keys,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    actionColumn,
    actions,
    ignoreFirstChild,
    onRowClickCallback,
    setSelectedRows,
    isAllRowsSelected,
    isCheckboxIntermediate,
    setIsAllRowsSelected,
    leftAlignFirstColumn = false,
    hasDeleteAccess = true,
    hasEditAccess = true,
    firstChildClassName = "justify-center",
}) => {
    const [deleteButton, setDeleteButton] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(false);

    const { t } = useTranslation();

    const renderHeaders = () => {
        return (
            headers &&
            headers.map((header, index) => {
                if (!header.role) {
                    return (
                        <th
                            key={index}
                            scope="col"
                            className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                        >
                            <div
                                className={`flex items-center ${firstChildConditionalReturn(
                                    index,
                                    firstChildClassName,
                                    "justify-center"
                                )}`}
                            >
                                {firstChildConditionalReturn(
                                    index,
                                    setSelectedRows && (
                                        <CheckBox
                                            checked={
                                                isCheckboxIntermediate ||
                                                isAllRowsSelected
                                            }
                                            onChangeHandler={() => {
                                                setSelectedRows(
                                                    (prevSelected) =>
                                                        prevSelected.map(
                                                            () =>
                                                                !isAllRowsSelected
                                                        )
                                                );
                                                setIsAllRowsSelected(
                                                    (prevVal) => !prevVal
                                                );
                                            }}
                                            isIntermediate={
                                                isCheckboxIntermediate
                                            }
                                        />
                                    )
                                )}
                                {!header.skipSort && (
                                    <i
                                        onClick={() =>
                                            toggleSort(
                                                header.name,
                                                sortBy,
                                                setSortBy,
                                                sortDirection,
                                                setSortDirection
                                            )
                                        }
                                        className={`fas ${getSortDirectionIcon(
                                            header.name,
                                            sortBy,
                                            sortDirection
                                        )} table-head-icon`}
                                    />
                                )}
                                <span className="ml-2">{header.label}</span>
                            </div>
                        </th>
                    );
                }
            })
        );
    };

    const handleDeleteClicked = (index) => {
        setDeleteButton(true);
        setSelectedIndex(index);
    };

    const handleConfirmDelete = async () => {
        return handleDelete(selectedIndex);
    };

    const getKeyValue = (row, key, index) => {
        //Checking for sub keys for example user.organization.id
        if (key.element) return key.element(row, index);
        let rowValue = key.subKey
            ? row?.[key.name]?.[key.subKey]
            : row?.[key.name];
        rowValue = key.parseMethod ? key.parseMethod(rowValue) : rowValue;
        return rowValue;
    };

    const firstChildConditionalReturn = (
        keysIndex,
        trueReturn,
        falseReturn
    ) => {
        return keysIndex === 0 && !ignoreFirstChild ? trueReturn : falseReturn;
    };

    const valueTranslation = (value, key) => {
        if (value instanceof Date) return longDateTime(value);
        if (
            typeof value === "string" &&
            (key.name === "type" || key.name === "entityType")
        )
            return t(toCamelCase(value));
        else return value || "---";
    };

    const renderActions = (row, index) => {
        if (actions) return actions(row);
        return (
            <>
                <DeleteButton
                    hide={!hasDeleteAccess}
                    onClick={() => handleDeleteClicked(index)}
                    tippy={true}
                />
                {handleEditClicked ? (
                    <EditButton
                        hide={!hasEditAccess}
                        onClick={() => handleEditClicked(index)}
                        tippy={true}
                    />
                ) : (
                    <Link
                        to={{
                            pathname: `/${purpose}/form`,
                            state: {
                                data: rows[index],
                            },
                        }}
                    >
                        <EditButton tippy={true} />
                    </Link>
                )}
            </>
        );
    };

    const renderRowList = () => {
        const image = IMAGE_MAP[purpose];
        return (
            rows &&
            rows.map((row, index) => {
                const td = keys?.map((key, keysIndex) => {
                    if (!key.role) {
                        const rowValue = getKeyValue(row, key, index);
                        return (
                            <td key={keysIndex} className="px-6 py-3">
                                <div
                                    className={`card-table-item flex items-center ${firstChildConditionalReturn(
                                        keysIndex,
                                        image
                                            ? "mr-8"
                                            : leftAlignFirstColumn
                                            ? ""
                                            : "justify-center",
                                        "justify-center"
                                    )}`}
                                >
                                    {firstChildConditionalReturn(
                                        keysIndex,
                                        image && (
                                            <>
                                                <LazyImage
                                                    src={
                                                        row.image ||
                                                        require(`../../assets/images/${image}`)
                                                            .default
                                                    }
                                                    className="table-user-img"
                                                    alt="UserImage"
                                                />
                                            </>
                                        ),
                                        <></>
                                    )}
                                    <span
                                        className={`${firstChildConditionalReturn(
                                            keysIndex,
                                            image ? "text-left" : "",
                                            ""
                                        )} ${
                                            key.defaultWidth
                                                ? "w-36 overflow-hidden block overflow-ellipsis whitespace-nowrap"
                                                : ""
                                        }`}
                                    >
                                        {valueTranslation(rowValue, key)}
                                    </span>
                                </div>
                            </td>
                        );
                    }
                });

                return (
                    <tr
                        key={index}
                        onClick={() =>
                            onRowClickCallback && onRowClickCallback(row)
                        }
                    >
                        {td}
                        {actionColumn && (
                            <td className="px-6 py-3 whitespace-wrap">
                                <div className="flex items-center justify-center">
                                    {renderActions(row, index)}
                                </div>
                            </td>
                        )}
                    </tr>
                );
            })
        );
    };

    return (
        <>
            <div className="flex flex-col mb-5">
                <div className="table-responsive action-wrapper shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center ">
                        <thead>
                            <tr>{renderHeaders()}</tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {renderRowList()}
                        </tbody>
                    </table>
                </div>
            </div>
            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("deleteTitle", { purpose })}
                message={t("deleteMessage", { purpose: purpose.slice(0, -1) })}
                action={{ submitAction: handleConfirmDelete }}
                type="delete"
                isSubmitting={isSubmitting}
            />
        </>
    );
};

export default CardTable;
